import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect, MouseEvent } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  ApplicationPermissionGet,
  ApplicationPermissionPut,
} from "../../../../../models/ApplicationPermission"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  noDevice: z.boolean(),
})

export function ApplicationPermissionDetails(props: {
  readonly permission: ApplicationPermissionGet
  readonly applicationId: string
  readonly user: UserInfo
  readonly setPermission: (updatedPermission: ApplicationPermissionGet) => void
}) {
  const { permission, applicationId, user, setPermission } = props

  const returnURL = `/applications/${applicationId}#permissions`

  const formMethods = useForm<ApplicationPermissionPut>({
    resolver: zodResolver(validationSchema),
  })

  const { handleSubmit, reset } = formMethods

  useEffect(() => {
    reset({
      name: permission.name,
      description: permission.description,
      noDevice: permission.noDevice || false,
    })
  }, [permission, reset])

  const onSubmit = useCallback(
    (values: ApplicationPermissionPut) =>
      digitalManagerApi
        .put<ApplicationPermissionGet>(
          `/applications/${applicationId}/permissions/${permission.id}`,
          {
            name: values.name,
            description: values.description,
            noDevice: values.noDevice,
          }
        )
        .then((res) => {
          toast.success("Permission updated")
          setPermission(res.data)
        })
        .catch(() => {
          toast.error("Cannot update permission")
          throw new Error("Cannot update permission")
        }),
    [applicationId, permission.id, setPermission]
  )

  const handleNameFieldChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleDescriptionFieldChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPut, "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleNoDeviceToggle = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionPut, "noDevice">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderNameField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPermissionPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof formMethods.formState.errors.name?.message === "string"
            ? { name: { message: formMethods.formState.errors.name.message } }
            : undefined
        }
      />
    ),
    [formMethods.formState.errors.name, handleNameFieldChange]
  )

  const renderDescriptionField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionPut, "description">
    }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleDescriptionFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof formMethods.formState.errors.description?.message === "string"
            ? {
                description: {
                  message: formMethods.formState.errors.description.message,
                },
              }
            : undefined
        }
      />
    ),
    [formMethods.formState.errors.description, handleDescriptionFieldChange]
  )

  const renderNoDeviceCheckbox = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionPut, "noDevice">
    }) => (
      <MUICheckBox
        {...field}
        id="noDevice"
        checked={field.value}
        handleToggle={handleNoDeviceToggle(field)}
      />
    ),
    [handleNoDeviceToggle]
  )

  if (!permission || !applicationId) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<ApplicationPermissionPut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{permission.name}</div>,
        editItem: (
          <Controller
            name="name"
            control={formMethods.control}
            render={renderNameField}
          />
        ),
      },
      {
        id: 1,
        label: "Description",
        displayItem: <div className="title">{permission.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={formMethods.control}
            render={renderDescriptionField}
          />
        ),
      },
      {
        id: 2,
        label: "No Device",
        displayItem: (
          <div className="title">{permission.noDevice?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="noDevice"
            control={formMethods.control}
            render={renderNoDeviceCheckbox}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
